import { FILTERS_NAME } from '@va/constants';
import { useAutocompletePagesSuggestions } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { TextFilter } from '@va/shared/feature-filters';
import { ApiOperators, FilterOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { FilterHook } from '../types';

export const useUrlFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new TextFilter({
        id: 'url',
        name: 'url',
        label: translate('filters.options.searchByUrl'),
        operator: FilterOperators.contains,
        operatorOptions: [FilterOperators.contains, FilterOperators.doesNotContain],
        ...params,
        inputProps: { label: translate('filter.urlName'), ...params },
      }),
    [params, translate],
  );
};

export const usePageUrlFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();
  const filter = useMemo(
    () =>
      new TextFilter({
        id: 'pageUrl',
        name: FILTERS_NAME.PAGE_URL,
        label: translate('filter.pageUrl'),
        operator: FilterOperators.is,
        multiSelectButtonLabel: translate('label.addNewUrl'),
        operatorOptions: [FilterOperators.is, FilterOperators.contains, FilterOperators.doesNotContain],
        operatorMapper: (operator) => {
          switch (operator) {
            case FilterOperators.contains:
              return ApiOperators.in;
            case FilterOperators.doesNotContain:
              return ApiOperators.notIn;
            case FilterOperators.is:
              return ApiOperators.eq;
            default:
              return ApiOperators.in;
          }
        },
        ...params,
        inputProps: {
          placeholder: (operator) => {
            if (operator === FilterOperators.is) {
              return translate('filters.placeholder.completePageUrl');
            }
            return translate('filters.placeholder.pageUrl');
          },
          ...params?.inputProps,
        },
        autocompleteOptionsFetcher: useAutocompletePagesSuggestions,
      }),
    [params, translate],
  );

  return filter;
};

export const useLandingPageUrlFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();
  const filter = useMemo(
    () =>
      new TextFilter({
        id: 'landingPageUrl',
        name: 'landingPageUrl',
        label: translate('filter.landingPageUrl'),
        operator: FilterOperators.contains,
        operatorOptions: [FilterOperators.contains, FilterOperators.doesNotContain, FilterOperators.is],
        operatorMapper: (operator) => {
          switch (operator) {
            case FilterOperators.contains:
              return ApiOperators.in;
            case FilterOperators.doesNotContain:
              return ApiOperators.notIn;
            case FilterOperators.is:
              return ApiOperators.eq;
            default:
              return ApiOperators.in;
          }
        },
        ...params,
        inputProps: {
          placeholder: translate('filters.placeholder.landingPageUrl'),
          ...params?.inputProps,
        },
      }),
    [params, translate],
  );

  return filter;
};
